import { DeleteOutlined } from "@ant-design/icons";
import { renderAudienceStatusTag } from "@di-devs/audience-feature-plugin/client";
import type { AudienceWithStatus } from "@di-devs/audience-feature-plugin/shared";
import { renderSearchConditionTag } from "@di-devs/member-feature-plugin/client";
import type { TableColumnsType } from "antd";
import { Button, Tooltip } from "antd";
import moment from "moment";

const sortOrder = [
	"eventOccasionId",
	"occasionId",
	"participationStatus",
	"occurrenceStartAt",
	"occurrenceEndAt",
	"isRegistered",
	"membershipStatus",
	"isFriends",
	"messages",
	"createdAtMin",
	"createdAtMax",
	"memberSinceMin",
	"memberSinceMax",
	"lastVisitMin",
	"lastVisitMax",
	"paymentMethod",
	"paymentCompletedAt",
	"address",
];

const getSortIndex = (key: string) => {
	if (key.startsWith("memberAttributeFilter_")) {
		const id = parseInt(key.replace("memberAttributeFilter_", ""), 10);
		return 1000 + (isNaN(id) ? 0 : id);
	}
	const index = sortOrder.indexOf(key);
	return index === -1 ? 999 : index;
};

interface GetMemberColumnsProps {
	memberAttributes: any[];
	onDelete?: (record: AudienceWithStatus) => void;
	publicSettings?: any;
}

export const getMemberColumns = ({
	memberAttributes,
	onDelete,
}: GetMemberColumnsProps): TableColumnsType<AudienceWithStatus> => {
	return [
		{
			title: "ID",
			dataIndex: "audienceGroupId",
			key: "audienceGroupId",
			align: "center",
			width: 100,
			sorter: (a: AudienceWithStatus, b: AudienceWithStatus) =>
				Number(a.audienceGroupId) - Number(b.audienceGroupId),
			sortDirections: ["ascend", "descend"],
			render: (audienceGroupId: number) => audienceGroupId ?? "ー",
		},
		{
			title: "オーディエンス名",
			dataIndex: "description",
			key: "description",
			align: "center",
			width: 150,
			sorter: (a: AudienceWithStatus, b: AudienceWithStatus) =>
				(a.description || "").localeCompare(b.description || ""),
			sortDirections: ["ascend", "descend"],
			render: (description: string) => description ?? "ー",
		},
		{
			title: "件数",
			dataIndex: "audienceCount",
			key: "audienceCount",
			align: "center",
			width: 50,
			sorter: (a: AudienceWithStatus, b: AudienceWithStatus) =>
				a.audienceCount - b.audienceCount,
			sortDirections: ["ascend", "descend"],
			render: (audienceCount: number) => audienceCount ?? "ー",
		},
		{
			title: "検索条件",
			dataIndex: "searchCondition",
			key: "searchCondition",
			align: "center",
			width: 350,
			render: (searchCondition: {
				categoryId: number;
				occasionId: number;
				isCampaign: boolean;
				address: string;
				hasWon: boolean;
				candidateAtMin: Date;
				candidateAtMax: Date;
				isRegistered: boolean;
				isFriends: boolean;
				messages: "unread" | "read";
				createdAtMin: Date;
				createdAtMax: Date;
				memberSinceMin: Date;
				memberSinceMax: Date;
			}) => (
				<div className="flex flex-col items-center gap-1">
					{searchCondition &&
						Object.entries(searchCondition)
							.sort((a, b) => getSortIndex(a[0]) - getSortIndex(b[0]))
							.map((entry) =>
								renderSearchConditionTag(entry, memberAttributes),
							)}
				</div>
			),
		},
		{
			title: "状態",
			dataIndex: "status",
			key: "status",
			align: "center",
			width: 100,
			render: (status: any) =>
				status ? renderAudienceStatusTag(status) : "ー",
		},
		{
			title: "作成日",
			dataIndex: "created",
			key: "createdAt",
			align: "center",
			width: 120,
			sorter: (a, b) => a.created - b.created,
			sortDirections: ["ascend", "descend"],
			render: (created: any) =>
				created ? (
					<>
						{moment.unix(created).format("YYYY年M月D日")}
						<br />
						{moment.unix(created).format("HH:mm")}
					</>
				) : (
					"ー"
				),
		},
		{
			title: "有効期限",
			dataIndex: "expireTimestamp",
			key: "expireTimestamp",
			align: "center",
			width: 120,
			sorter: (a: any, b: any) => a.expireTimestamp - b.expireTimestamp,
			sortDirections: ["ascend", "descend"],
			render: (expireTimestamp: any) =>
				expireTimestamp ? (
					<>
						{moment.unix(expireTimestamp).format("YYYY年M月D日")}
						<br />
						{moment.unix(expireTimestamp).format("HH:mm")}
					</>
				) : (
					"ー"
				),
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			align: "center",
			width: 50,
			render: (_: any, record: AudienceWithStatus) => (
				<>
					{onDelete && (
						<Tooltip title="削除" placement="top">
							<Button
								className="m-1"
								icon={<DeleteOutlined />}
								danger
								onClick={() => {
									onDelete(record);
								}}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
};

export const getEventColumns = ({
	memberAttributes,
	onDelete,
	events,
}: {
	memberAttributes: any[];
	onDelete?: (record: AudienceWithStatus) => void;
	events?: any[];
}): TableColumnsType<AudienceWithStatus> => {
	const eventTitleMap = new Map<string, string>();
	if (events && events.length > 0) {
		events.forEach((e: any) => {
			if (e.eventOccasionId) {
				eventTitleMap.set(String(e.eventOccasionId), e.title);
			}
			if (e.occasionId) {
				eventTitleMap.set(String(e.occasionId), e.title);
			}
		});
	}

	return [
		{
			title: "ID",
			dataIndex: "audienceGroupId",
			key: "audienceGroupId",
			align: "center",
			width: 100,
			render: (val) => val ?? "ー",
		},
		{
			title: "オーディエンス名",
			dataIndex: "description",
			key: "description",
			align: "center",
			width: 150,
			render: (val) => val ?? "ー",
		},
		{
			title: "件数",
			dataIndex: "audienceCount",
			key: "audienceCount",
			align: "center",
			width: 80,
			render: (val) => val ?? "ー",
		},
		{
			title: "検索条件",
			dataIndex: "searchCondition",
			key: "searchCondition",
			align: "center",
			width: 350,
			render: (searchCondition: any) => {
				let displayCondition = searchCondition;
				if (searchCondition && eventTitleMap.size > 0) {
					displayCondition = { ...searchCondition };
					if (displayCondition.eventOccasionId) {
						const title = eventTitleMap.get(
							String(displayCondition.eventOccasionId),
						);
						if (title) displayCondition.eventOccasionId = title;
					}
					if (displayCondition.occasionId) {
						const title = eventTitleMap.get(
							String(displayCondition.occasionId),
						);
						if (title) displayCondition.occasionId = title;
					}
				}
				return (
					<div className="flex flex-col items-center gap-1">
						{displayCondition &&
							Object.entries(displayCondition)
								.sort((a, b) => getSortIndex(a[0]) - getSortIndex(b[0]))
								.map((entry) =>
									renderSearchConditionTag(entry as any, memberAttributes),
								)}
					</div>
				);
			},
		},
		{
			title: "状態",
			dataIndex: "status",
			key: "status",
			align: "center",
			width: 100,
			render: (status) => (status ? renderAudienceStatusTag(status) : "ー"),
		},
		{
			title: "作成日",
			dataIndex: "created",
			key: "created",
			align: "center",
			width: 120,
			render: (created) =>
				created ? moment.unix(created).format("YYYY/MM/DD HH:mm") : "ー",
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			align: "center",
			width: 50,
			render: (_: any, record: AudienceWithStatus) => (
				<>
					{onDelete && (
						<Tooltip title="削除" placement="top">
							<Button
								icon={<DeleteOutlined />}
								danger
								onClick={() => onDelete(record)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
};
